import { CollateralTypes } from '@common/constants/client'
import {
  BBC_AGING_REPORT,
  IApRollforwardData,
  IARAPChartDropdownOptions,
  IArIneligibilityData,
  IARRollforwardGraphData,
  IExtraReserveData,
  IPreviousCollateralDateOptions,
  IArRollforwardData,
  IBorrowingBase,
  IClientCollateral,
  IInventoryIneligibilityCustomRule,
  IInventoryIneligibilityData,
  IInventoryIneligibilityField,
  IMasterInvoicesData,
  IReserve,
  IInventoryIneligibleStats,
  IARIneligibleStats,
  IARIneligibleStatsDetailsInvoice,
  IArSummaryStatsData,
  IApSummaryStatsData,
  IARAPChart,
  IInventoryDistributionAnalysisData,
  IInventoryDistributionAnalysisCategories,
  INVENTORY_AGING_FIELDS,
  InventoryAgingField,
  OngoingReportingType,
} from '@common/interfaces/bbc'
import { IOptionDropdown } from '@common/interfaces/options'
import { combineReducers } from 'redux'
import { createKeyedLoadingDataReducer, createLoadingDataReducer } from '../../helpers/redux'
import { IAction, IActionWithParams } from '../apiMiddleware'
import {
  UPDATE_INVENTORY_INELIGIBILITY_FIELDS_REQUEST,
  LIST_INVENTORY_ELIGIBLE_CUSTOM_RULES_REQUEST,
  LIST_INVENTORY_ELIGIBLE_CUSTOM_RULES_SUCCESS,
  BULK_UPDATE_AR_ELIGIBILITY_SUCCESS,
  BULK_UPDATE_MASTER_INVENTORY_SUCCESS,
  UPDATE_MASTER_INVOICE_SUCCESS,
} from '../clientInfo/actions'
import * as actions from './actions'

/** Saves on the Collateral Eligibility page that change what the client collateral calculation reads. */
const ELIGIBILITY_CHANGE_ACTIONS = new Set([
  actions.UPDATE_AR_INELIGIBILITY_SUCCESS,
  BULK_UPDATE_AR_ELIGIBILITY_SUCCESS,
  UPDATE_MASTER_INVOICE_SUCCESS,
  actions.UPDATE_MASTER_INVENTORY_SUCCESS,
  BULK_UPDATE_MASTER_INVENTORY_SUCCESS,
  actions.ACCEPT_PREDICTED_INVENTORY_TYPE_SUCCESS,
  actions.UPDATE_INVENTORY_INELIGIBLE_CATEGORIES_SUCCESS,
  actions.CREATE_INVENTORY_INELIGIBLE_FIELDS_SUCCESS,
  actions.UPDATE_INVENTORY_INELIGIBLE_FIELDS_SUCCESS,
  actions.DELETE_INVENTORY_INELIGIBLE_FIELDS_SUCCESS,
  actions.CREATE_INVENTORY_INELIGIBLE_CUSTOM_RULES_SUCCESS,
  actions.UPDATE_INVENTORY_INELIGIBLE_CUSTOM_RULES_SUCCESS,
  actions.DELETE_INVENTORY_INELIGIBLE_CUSTOM_RULES_SUCCESS,
])

/** Eligibility saves against the calculations that read them, so a save made while one is in
 * flight keeps the figures out of date until a calculation started after it lands. */
interface IClientCollateralFreshness {
  revision: number
  calculatedRevision: number
  /** The revision each in-flight calculation read, by request id. */
  pendingRevisions: Record<number, number>
}

const INITIAL_CLIENT_COLLATERAL_FRESHNESS: IClientCollateralFreshness = {
  revision: 0,
  calculatedRevision: 0,
  pendingRevisions: {},
}

/** The collateral types whose reserves live in the `reserves` slice; AP has its own `apReserves`. */
export type ReserveSliceType = CollateralTypes.ar | CollateralTypes.inventory
export const RESERVE_SLICE_TYPES: ReserveSliceType[] = [
  CollateralTypes.ar,
  CollateralTypes.inventory,
]

export type PreviousClientCollateralsState = Record<
  BBC_AGING_REPORT,
  IPreviousCollateralDateOptions[]
>

const EMPTY_PREVIOUS_CLIENT_COLLATERALS: PreviousClientCollateralsState = {
  [BBC_AGING_REPORT.AR]: [],
  [BBC_AGING_REPORT.AP]: [],
}

const AGING_REPORT_BY_REPORT_TYPE: Partial<Record<OngoingReportingType, BBC_AGING_REPORT>> = {
  [OngoingReportingType.AR]: BBC_AGING_REPORT.AR,
  [OngoingReportingType.AP]: BBC_AGING_REPORT.AP,
}

const setPreviousClientCollaterals = (
  state: PreviousClientCollateralsState,
  reportType: OngoingReportingType | undefined,
  data: IPreviousCollateralDateOptions[],
): PreviousClientCollateralsState => {
  const agingReport = reportType ? AGING_REPORT_BY_REPORT_TYPE[reportType] : undefined
  if (!agingReport) {
    return { [BBC_AGING_REPORT.AR]: data, [BBC_AGING_REPORT.AP]: data }
  }
  return { ...state, [agingReport]: data }
}

export default combineReducers({
  loadingArray(state: string[] = [], action: IAction): string[] {
    switch (action.type) {
      case actions.SUBMIT_AR_INELIGIBILITY_REQUEST:
      case actions.INVENTORY_INELIGIBLE_SUBMIT_REQUEST:
      case actions.CALCULATE_CLIENT_COLLATERAL_REQUEST:
        return [...state, action.type]
      case actions.SUBMIT_AR_INELIGIBILITY_SUCCESS:
      case actions.SUBMIT_AR_INELIGIBILITY_FAILURE:
      case actions.INVENTORY_INELIGIBLE_SUBMIT_SUCCESS:
      case actions.INVENTORY_INELIGIBLE_SUBMIT_FAILURE:
      case actions.CALCULATE_CLIENT_COLLATERAL_SUCCESS:
      case actions.CALCULATE_CLIENT_COLLATERAL_FAILURE:
        return state.filter(
          (item) => item !== action.type.replace(/(SUCCESS|FAILURE)/gi, 'REQUEST'),
        )
      default:
        return state
    }
  },
  bbc(state: IBorrowingBase = null, action: IAction): IBorrowingBase {
    switch (action.type) {
      case actions.GET_BBC_FILES_SUCCESS: {
        const files = action.data as IBorrowingBase['files']
        return {
          ...(state || {}),
          files,
        } as IBorrowingBase
      }
      case actions.SUBMIT_BBC_FILES_SUCCESS: {
        // SubmitFiles returns the full borrowing base with freshly recomputed
        // fileSheets (headerRow/sampleData). Commit those so the file-select and
        // inventory column-mapping UIs refresh in place after a reselect/clear —
        // getBBCFiles only refreshes `files`, leaving `fileSheets` stale otherwise.
        const data = action.data as IBorrowingBase
        if (!data?.fileSheets) {
          return state
        }
        return {
          ...(state || {}),
          fileSheets: data.fileSheets,
        } as IBorrowingBase
      }
      case actions.SHOW_SUCCESS:
        return action.data as IBorrowingBase
      case actions.HIDE:
        return null
      case actions.UPDATE_BBC_PDF_SUCCESS: {
        const data = action.data as { boxLink: string | null }
        return {
          ...state,
          boxLink: data.boxLink,
        }
      }
      default:
        return state
    }
  },
  clientCollateralFreshness(
    state: IClientCollateralFreshness = INITIAL_CLIENT_COLLATERAL_FRESHNESS,
    action: IActionWithParams<actions.ICalculateClientCollateralParams>,
  ): IClientCollateralFreshness {
    if (ELIGIBILITY_CHANGE_ACTIONS.has(action.type)) {
      return { ...state, revision: state.revision + 1 }
    }
    switch (action.type) {
      case actions.CALCULATE_CLIENT_COLLATERAL_REQUEST:
        if (action.requestId === undefined) return state
        return {
          ...state,
          pendingRevisions: {
            ...state.pendingRevisions,
            [action.requestId]: action.params?.eligibilityRevision ?? state.revision,
          },
        }
      case actions.CALCULATE_CLIENT_COLLATERAL_SUCCESS:
      case actions.CALCULATE_CLIENT_COLLATERAL_FAILURE: {
        if (action.requestId === undefined) return state
        const { [action.requestId]: readRevision, ...pendingRevisions } = state.pendingRevisions
        const isCalculated =
          action.type === actions.CALCULATE_CLIENT_COLLATERAL_SUCCESS && readRevision !== undefined
        return {
          ...state,
          calculatedRevision: isCalculated
            ? Math.max(state.calculatedRevision, readRevision)
            : state.calculatedRevision,
          pendingRevisions,
        }
      }
      // Fresh without rewinding: a revision captured before the unload never outranks the next BBC's.
      case actions.HIDE:
        return {
          revision: state.revision,
          calculatedRevision: state.revision,
          pendingRevisions: {},
        }
      default:
        return state
    }
  },
  fields: createLoadingDataReducer<{ data: IInventoryIneligibilityField[] }>(
    [actions.LIST_INVENTORY_INELIGIBLE_FIELDS_REQUEST],
    [
      actions.CREATE_INVENTORY_INELIGIBLE_FIELDS_REQUEST,
      UPDATE_INVENTORY_INELIGIBILITY_FIELDS_REQUEST,
      actions.UPDATE_INVENTORY_INELIGIBLE_FIELDS_REQUEST,
      actions.DELETE_INVENTORY_INELIGIBLE_FIELDS_REQUEST,
    ],
  ),
  inventoryIneligibilityData: createLoadingDataReducer<IInventoryIneligibilityData>(
    [actions.LIST_MASTER_INVENTORY_REQUEST],
    [actions.UPDATE_MASTER_INVENTORY_REQUEST],
  ),
  clientCollaterals(state: IClientCollateral[] = [], action: IAction): IClientCollateral[] {
    switch (action.type) {
      case actions.GET_BBC_SUMMARY_STATS_SUCCESS:
        return action.data as IClientCollateral[]
      default:
        return state
    }
  },
  previousClientCollaterals(
    state: PreviousClientCollateralsState = EMPTY_PREVIOUS_CLIENT_COLLATERALS,
    action: IActionWithParams<{ reportType?: OngoingReportingType }>,
  ): PreviousClientCollateralsState {
    switch (action.type) {
      case actions.GET_PREVIOUS_CLIENT_COLLATERALS_SUCCESS: {
        const data = action.data as { data: IPreviousCollateralDateOptions[] }
        return setPreviousClientCollaterals(state, action.params?.reportType, data.data)
      }
      case actions.GET_PREVIOUS_CLIENT_COLLATERALS_FAILURE:
        return setPreviousClientCollaterals(state, action.params?.reportType, [])
      case actions.HIDE:
        return EMPTY_PREVIOUS_CLIENT_COLLATERALS
      default:
        return state
    }
  },
  arRollforward: createLoadingDataReducer<IArRollforwardData>(
    [actions.GET_BBC_AR_ROLLFORWARD_STATS_REQUEST, actions.GET_BBC_AR_ROLLFORWARD_DETAILS_REQUEST],
    [actions.EXPORT_BBC_AR_ROLLFORWARD_REQUEST],
  ),
  arRollforwardGraph: createLoadingDataReducer<IARRollforwardGraphData>([
    actions.GET_BBC_AR_ROLLFORWARD_GRAPH_REQUEST,
  ]),
  apRollforward: createLoadingDataReducer<IApRollforwardData>(
    [actions.GET_BBC_AP_ROLLFORWARD_STATS_REQUEST, actions.GET_BBC_AP_ROLLFORWARD_DETAILS_REQUEST],
    [actions.EXPORT_BBC_AP_ROLLFORWARD_REQUEST],
  ),
  // Keyed so the AR and Inventory reserve tables, which render side by side, keep their own rows.
  reserves: createKeyedLoadingDataReducer<{ data: IReserve[] }, ReserveSliceType>(
    [actions.LIST_BBC_RESERVE_REQUEST, actions.LIST_CLIENT_RESERVE_REQUEST],
    'type',
    RESERVE_SLICE_TYPES,
    [
      actions.CREATE_BBC_RESERVE_REQUEST,
      actions.UPDATE_BBC_RESERVE_REQUEST,
      actions.DELETE_BBC_RESERVE_REQUEST,
      actions.CREATE_CLIENT_RESERVE_REQUEST,
      actions.UPDATE_CLIENT_RESERVE_REQUEST,
      actions.DELETE_CLIENT_RESERVE_REQUEST,
      actions.DELETE_ALL_BBC_RESERVES_REQUEST,
    ],
  ),
  customRules: createLoadingDataReducer<IInventoryIneligibilityCustomRule[]>(
    [
      actions.LIST_INVENTORY_INELIGIBLE_CUSTOM_RULES_REQUEST,
      LIST_INVENTORY_ELIGIBLE_CUSTOM_RULES_REQUEST,
    ],
    [
      actions.CREATE_INVENTORY_INELIGIBLE_CUSTOM_RULES_REQUEST,
      actions.UPDATE_INVENTORY_INELIGIBLE_CUSTOM_RULES_REQUEST,
      actions.DELETE_INVENTORY_INELIGIBLE_CUSTOM_RULES_REQUEST,
    ],
  ),
  customRulesLabels(state: string[] = [], action: IAction): string[] {
    switch (action.type) {
      case actions.LIST_INVENTORY_INELIGIBLE_CUSTOM_RULES_LABELS_SUCCESS:
      case LIST_INVENTORY_ELIGIBLE_CUSTOM_RULES_SUCCESS:
        return action.data as string[]
      default:
        return state
    }
  },
  customRulesValues(
    state: { [key: string]: string[] } = {},
    action: IAction & { field: string },
  ): { [key: string]: string[] } {
    switch (action.type) {
      case actions.LIST_INVENTORY_INELIGIBLE_CUSTOM_RULES_VALUES_SUCCESS:
        return {
          ...state,
          [action.field]: action.data as string[],
        }
      default:
        return state
    }
  },
  inventoryIneligibleStats: createLoadingDataReducer<IInventoryIneligibleStats>([
    actions.LIST_INVENTORY_INELIGIBLE_STATS_SUMMARY_REQUEST,
    actions.LIST_INVENTORY_INELIGIBLE_STATS_REASON_REQUEST,
    actions.LIST_INVENTORY_INELIGIBLE_STATS_DETAIL_REQUEST,
  ]),
  arIneligibleStats: createLoadingDataReducer<{ data: IARIneligibleStats[] }>([
    actions.LIST_AR_INELIGIBLE_STATS_REQUEST,
    actions.LIST_AR_INELIGIBLE_STATS_DETAILS_REQUEST,
    actions.LIST_AR_INELIGIBLE_STATS_DETAILS_INVOICES_REQUEST,
  ]),
  ineligibleInvoices: createLoadingDataReducer<{ data: IARIneligibleStatsDetailsInvoice[] }>([
    actions.LIST_INELIGIBLE_INVOICES_REQUEST,
  ]),
  arSummaryStats: createLoadingDataReducer<IArSummaryStatsData>([
    actions.GET_BBC_AR_SUMMARY_STATS_REQUEST,
    actions.GET_BBC_AR_DETAIL_STATS_REQUEST,
  ]),
  apSummaryStats: createLoadingDataReducer<IApSummaryStatsData>([
    actions.GET_BBC_AP_SUMMARY_STATS_REQUEST,
    actions.GET_BBC_AP_DETAIL_STATS_REQUEST,
  ]),

  // Keyed by `agingReport` (AR/AP) — Amendment Analysis' Collateral Overview
  // mounts an AR and an AP `ARAPSummaryOverTime` simultaneously, and a single
  // unkeyed slot let whichever `_SUCCESS` landed last overwrite the other's
  // data, so both charts rendered the same report.
  arapChartData: createKeyedLoadingDataReducer<IARAPChart, BBC_AGING_REPORT>(
    [actions.GET_BBC_AR_AP_CHART_DATA_REQUEST],
    'agingReport',
    [BBC_AGING_REPORT.AR, BBC_AGING_REPORT.AP],
  ),

  // Keyed for the same reason as `arapChartData`: the BBC summary mounts AR and AP tables together.
  arapChartDataDropdownOptions: createKeyedLoadingDataReducer<
    IARAPChartDropdownOptions,
    BBC_AGING_REPORT
  >([actions.GET_AR_AP_CHART_DROPDOWN_OPTIONS_REQUEST], 'agingReport', [
    BBC_AGING_REPORT.AR,
    BBC_AGING_REPORT.AP,
  ]),

  apReserves: createLoadingDataReducer<IExtraReserveData>(
    [actions.LIST_EXTRA_RESERVES_REQUEST],
    [actions.CREATE_EXTRA_RESERVES_REQUEST, actions.UPDATE_EXTRA_RESERVES_REQUEST],
  ),
  arIneligibilityData: createLoadingDataReducer<IArIneligibilityData>(
    [actions.LIST_AR_INELIGIBILITY_REQUEST],
    [actions.UPDATE_AR_INELIGIBILITY_REQUEST],
  ),
  masterInvoicesBbcData: createLoadingDataReducer<IMasterInvoicesData>([
    actions.LIST_MASTER_INVOICES_BBC_REQUEST,
  ]),
  inventoryDistributionData: createLoadingDataReducer<IInventoryDistributionAnalysisData>([
    actions.LIST_INVENTORY_DISTRIBUTION_ANALYSIS_TYPE_REQUEST,
  ]),
  inventoryDistributionCategories:
    createLoadingDataReducer<IInventoryDistributionAnalysisCategories>([
      actions.LIST_INVENTORY_DISTRIBUTION_ANALYSIS_CATEGORIES_REQUEST,
    ]),
  /** Keyed by distribution field so the two aging charts do not share one slot. */
  inventoryAgingDistribution: createKeyedLoadingDataReducer<
    IInventoryDistributionAnalysisData,
    InventoryAgingField
  >([actions.LIST_INVENTORY_AGING_DISTRIBUTION_REQUEST], 'field', INVENTORY_AGING_FIELDS),
  foreignCountryDropdown(state: IOptionDropdown[] = [], action: IAction): IOptionDropdown[] {
    switch (action.type) {
      case actions.LIST_FOREIGN_COUNTRIES_DROPDOWN_SUCCESS:
        return action.data as IOptionDropdown[]
      default:
        return state
    }
  },
})
